import React from 'react';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography, Drawer } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ScienceIcon from '@mui/icons-material/Science';
import BalanceIcon from '@mui/icons-material/Balance';
import BiotechIcon from '@mui/icons-material/Biotech';
import GasIcon from '@mui/icons-material/LocalGasStation';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { colors, layout } from '../theme/designSystem';

interface SidebarProps {
  activeItem?: string;
  onMenuClick?: (itemId: string) => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  isMobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  activeItem = 'dashboard', 
  onMenuClick,
  mobileOpen = false,
  onMobileClose,
  isMobile = false
}) => {
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { id: 'substrates', label: 'Substrates', icon: <BalanceIcon /> },
    { id: 'labReport',  label: 'Lab Report',  icon: <BiotechIcon /> },
    { id: 'gasMonitor', label: 'Gas Monitor',  icon: <GasIcon /> },
    { id: 'fostac',      label: 'FOSTAC & Gas Quality', icon: <ScienceIcon /> },
    { id: 'dailyBiogas',    label: 'Daily Biogas Monitor',  icon: <MonitorHeartIcon /> },
    { id: 'monthlyBilling', label: 'Monthly Bill Report',   icon: <ReceiptLongIcon /> },
    { id: 'flaredGasReport', label: 'Flared Gas Report',  icon: <LocalFireDepartmentIcon /> },
    { id: 'weeklyMonthlyReport', label: 'Weekly/Monthly Report', icon: <AssessmentIcon /> },
    { id: 'settings',  label: 'Settings',    icon: <SettingsIcon /> },
  ];

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        background: layout.sidebar.background,
        padding: '24px 16px',
      }}
    >
      {/* Logo/Brand */}
      <Box sx={{ mb: 4, px: 1 }}>
        <Typography
          variant="h5"
          sx={{
            color: '#ffffff',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <ScienceIcon sx={{ fontSize: '2rem', color: colors.primary[400] }} />
          BioGas
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: '#94a3b8',
            display: 'block',
            mt: 0.5,
          }}
        >
          Plant Management
        </Typography>
      </Box>

      {/* Navigation */}
      <List sx={{ p: 0 }}>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.id}
            selected={activeItem === item.id}
            onClick={() => onMenuClick?.(item.id)}
            sx={{
              height: '40px',
              borderRadius: '8px',
              mb: 0.5,
              color: activeItem === item.id ? '#ffffff' : '#cbd5e1',
              background:
                activeItem === item.id
                  ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                  : 'transparent',
              '&:hover': {
                background:
                  activeItem === item.id
                    ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                    : 'rgba(255,255,255,0.1)',
              },
              '& .MuiListItemIcon-root': {
                minWidth: '36px',
                color: activeItem === item.id ? '#ffffff' : '#cbd5e1',
              },
            }}
          >
            <ListItemIcon sx={{ fontSize: '20px' }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: layout.sidebar.width,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Box
      sx={{
        width: layout.sidebar.width,
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1200,
        display: { xs: 'none', md: 'block' },
      }}
    >
      {drawerContent}
    </Box>
  );
};

export default Sidebar;
